import { api, apiCall } from './client'
import type {
  ApiResponse,
  Message,
  Conversation,
} from '../../types'

export interface MessageListParams {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  senderId?: number
  receiverId?: number
  patientId?: number
  conversationId?: string
}

export interface CreateMessageData {
  content: string
  receiverId: number
  attachmentUrl?: string
  attachmentType?: string
}

export interface UpdateMessageData {
  content?: string
  status?: 'sent' | 'delivered' | 'read'
}

export const messagesService = {
  async list(params?: MessageListParams): Promise<ApiResponse<Message[]>> {
    return apiCall(api.get<ApiResponse<Message[]>>('/messages', { params }))
  },

  async getById(id: number): Promise<ApiResponse<Message>> {
    return apiCall(api.get<ApiResponse<Message>>(`/messages/${id}`))
  },

  async create(payload: CreateMessageData): Promise<ApiResponse<Message>> {
    return apiCall(api.post<ApiResponse<Message>>('/messages', payload))
  },

  async update(
    id: number,
    payload: UpdateMessageData
  ): Promise<ApiResponse<Message>> {
    return apiCall(
      api.patch<ApiResponse<Message>>(`/messages/${id}`, payload)
    )
  },

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiCall(api.delete<ApiResponse<void>>(`/messages/${id}`))
  },

  // GET /messages?receiverId=userId2 — returns MessageResponseDto[]
  async getConversation(
    _userId1: string,
    userId2: string | number,
    params?: Omit<MessageListParams, 'conversationId'>
  ): Promise<ApiResponse<Message[]>> {
    return apiCall(
      api.get<ApiResponse<Message[]>>('/messages', {
        params: { ...params, receiverId: Number(userId2) },
      })
    )
  },

  // GET /messages/conversations — userId from JWT
  async getConversations(_userId: string): Promise<ApiResponse<Conversation[]>> {
    return apiCall(
      api.get<ApiResponse<Conversation[]>>('/messages/conversations')
    )
  },

  async markConversationAsRead(
    conversationId: string
  ): Promise<ApiResponse<null>> {
    return apiCall(
      api.patch<ApiResponse<null>>(
        `/messages/conversations/${conversationId}/read`
      )
    )
  },

  // PATCH /messages/:id/read
  async markAsRead(
    _userId: string,
    messageIds: number[]
  ): Promise<ApiResponse<{ count: number }>> {
    // Mark each message individually
    await Promise.all(
      messageIds.map(id =>
        apiCall(api.patch<ApiResponse<null>>(`/messages/${id}/read`))
      )
    )
    return { success: true, message: 'ok', data: { count: messageIds.length }, timestamp: new Date().toISOString() }
  },

  // GET /messages/unread-count — userId from JWT
  async getUnreadCount(
    _userId: string
  ): Promise<ApiResponse<{ count: number }>> {
    return apiCall(
      api.get<ApiResponse<{ count: number }>>('/messages/unread-count')
    )
  },
}

export type MessagesService = typeof messagesService
